import { useAtom } from "jotai";
import { projectDetailsAtom } from "../atoms/projectDetailsAtom";
import i18n from "../../i18n";
import { useMantineColorScheme } from "@mantine/core";
import { trimTrailingZeros } from "../../constants/helper";
import { parseISOToLocalDateAtMidnightUTC } from "../dateHelper";

const MONTH_INDEX_DECEMBER = 11;
const LAST_DAY_OF_DECEMBER = 31;
const END_OF_DAY_HOUR_UTC = 23;
const END_OF_DAY_MINUTE_UTC = 59;
const END_OF_DAY_SECOND_UTC = 59;
const END_OF_DAY_MILLISECOND_UTC = 999;

const getTranslation = (path, defaultValue = "") => {
  return i18n.t(path, {
    defaultValue:
      defaultValue !== undefined && defaultValue !== null ? defaultValue : path,
  });
};

const getCurrency = (projectDetails) => {
  return projectDetails?.projectDTO?.currencyCode || "$";
};

// Use this hook inside React components only
const useCurrency = () => {
  const [projectDetails] = useAtom(projectDetailsAtom);
  return projectDetails?.projectDTO?.currencyCode || "$";
};

// Use this hook inside React components only
const useIsLightMode = () => {
  const { colorScheme } = useMantineColorScheme();
  return colorScheme === "light";
};

const removeLeadingZeros = (value) => {
  const cleaned = value.replace(/^0+/, "");
  return cleaned === "" ? "0" : cleaned;
};


const sanitizeDigits = (value) => {
  if (value === undefined || value === null) return "";
  let cleaned = String(value).trim();
  if (cleaned === "") return "";
  // Keep digits and dots only
  cleaned = cleaned.replace(/[^0-9.]/g, "");
  if (cleaned === "") return "";
  // If starts with a dot, prefix a 0 (".5" -> "0.5")
  if (cleaned.startsWith(".")) cleaned = `0${cleaned}`;
  // Allow only the first decimal point
  const firstDotIndex = cleaned.indexOf(".");
  if (firstDotIndex !== -1) {
    const before = cleaned.slice(0, firstDotIndex + 1);
    const after = cleaned.slice(firstDotIndex + 1).replace(/\./g, "");
    cleaned = before + after;
  }
  // Split integer and fractional parts
  let [intPart, fracPart] = cleaned.split(".");
  // Remove leading zeros but keep a single zero if all zeros
  intPart = intPart.replace(/^0+(?=\d)/, "");
  if (intPart === "") intPart = "0";
  // Reconstruct
  return fracPart !== undefined ? `${intPart}.${fracPart}` : intPart;
};

const generateTimeSeries = () => {
  const times = [];
  for (let i = 0; i < 24 * 60; i += 15) {
    const hours = Math.floor(i / 60)
      .toString()
      .padStart(2, "0");
    const minutes = (i % 60).toString().padStart(2, "0");
    times.push({ value: `${hours}:${minutes}`, label: `${hours}:${minutes}` });
  }
  return times;
};

const formatCompactNumber = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  const trimTrailingZeros = (num) =>
    Number(num.toFixed(2)).toLocaleString("en-US", { maximumFractionDigits: 2 });
  if (abs >= 1e12) return `${sign}${trimTrailingZeros(abs / 1e12)}T`;
  if (abs >= 1e9) return `${sign}${trimTrailingZeros(abs / 1e9)}B`;
  if (abs >= 1e6) return `${sign}${trimTrailingZeros(abs / 1e6)}M`;
  if (abs >= 1e3) return `${sign}${trimTrailingZeros(abs / 1e3)}k`;
  return trimTrailingZeros(abs) === "0" ? "0" : `${sign}${trimTrailingZeros(abs)}`;
};

const isLevelizedCostOfEnergyTitle = (title = "") =>
  String(title).toLowerCase().includes("levelized cost of");

const formatLcoeUpToFiveDecimals = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;
  const fixed = numeric.toFixed(4);
  if (!fixed.includes(".")) return fixed;

  const [intPartRaw, fracPartRaw = ""] = fixed.split(".");
  const trimmedFrac = trimTrailingZeros(fracPartRaw);
  const intPart = Number(intPartRaw).toLocaleString("en-US");

  if (!trimmedFrac) {
    return intPart;
  }
  return `${intPart}.${trimmedFrac}`;
};

const formatValueByChartTitle = (title, value) => {
  if (!isLevelizedCostOfEnergyTitle(title)) {
    return formatCompactNumber(value);
  }
  return formatLcoeUpToFiveDecimals(value);
};

const formatTooltipValueByChartTitle = (title, value) => {
  if (!isLevelizedCostOfEnergyTitle(title)) {
    return value?.toLocaleString?.("en-US") ?? value ?? "";
  }
  return formatLcoeUpToFiveDecimals(value);
};

const formatDisplayNumber = (value) => {
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return numeric.toLocaleString?.("en-US") ?? String(numeric);
  }
  return value ?? "";
};

const formatEnUsValue = (value) => {
  if (value === null || value === undefined || value === "") return value;
  if (typeof value !== "number" || Number.isNaN(value)) return value;
  return value.toLocaleString("en-US", { maximumFractionDigits: 2 });
};

const roundToMaxTwoDecimals = (value) => {
  if (typeof value !== "number" || Number.isNaN(value)) return value;
  if (Number.isInteger(value)) return value;
  return Math.round(value * 100) / 100;
};

const toUtcISOStringWithStartTime = (value, startTime) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const [hoursRaw, minutesRaw] = String(startTime || "00:00").split(":");
  const hours = Number.parseInt(hoursRaw, 10);
  const minutes = Number.parseInt(minutesRaw, 10);
  const safeHours = Number.isInteger(hours) && hours >= 0 && hours <= 23 ? hours : 0;
  const safeMinutes = Number.isInteger(minutes) && minutes >= 0 && minutes <= 59 ? minutes : 0;

  return new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      safeHours,
      safeMinutes,
      0,
      0
    )
  ).toISOString();
};

const getUtcMidnightIso = (startDate) => {
  if (!startDate) return null;
  const date = startDate instanceof Date ? startDate : new Date(startDate);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)
  ).toISOString();
};

const getDefaultEvcsStartDate = (loadStartDate) => {
  if (!loadStartDate) return null;
  const parsedDate =
    loadStartDate instanceof Date
      ? new Date(loadStartDate)
      : parseISOToLocalDateAtMidnightUTC(loadStartDate);
  if (parsedDate instanceof Date && !Number.isNaN(parsedDate.getTime())) {
    return parsedDate;
  }

  const fallbackLoadDate = new Date(loadStartDate);
  return Number.isNaN(fallbackLoadDate.getTime()) ? null : fallbackLoadDate;
};

const validatePasswordAgainstNames = ({
  password = '',
  firstName = '',
  lastName = '',
  options = {}
}) => {
  const {
    minLength = 15,
    enableLeetCheck = true,
    enableReverseCheck = true
  } = options;

  const reasons = [];
  const trimmedPwd = password.trim();

  const complexityRegex = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).+$/;

  if (trimmedPwd.length < minLength) {
    reasons.push(`Password must be at least ${minLength} characters.`);
  }
  if (!complexityRegex.test(trimmedPwd)) {
    reasons.push('Password must include upper, lower, digit and special character (#?!@$%^&*-).');
  }

  const sanitize = (str) => str
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ');

  const meaningfulParts = (name) => sanitize(name)
    .split(' ')
    .filter(Boolean)
    .filter(p => p.length >= 3);

  const pwdSanitized = sanitize(trimmedPwd);

  const allNameParts = [
    ...meaningfulParts(firstName),
    ...meaningfulParts(lastName)
  ];

  const fullNames = [];
  const fnSan = sanitize(firstName);
  const lnSan = sanitize(lastName);
  if (fnSan.length >= 3) fullNames.push(fnSan);
  if (lnSan.length >= 3) fullNames.push(lnSan);

  const leetMap = { '0': 'o', '1': 'l', '3': 'e', '4': 'a', '5': 's', '7': 't' };
  const deLeet = (str) => str.replace(/[013457]/g, ch => leetMap[ch] || ch);
  const pwdDeLeet = enableLeetCheck ? deLeet(pwdSanitized) : pwdSanitized;

  const containsAny = (source) => {
    for (const token of [...allNameParts, ...fullNames]) {
      if (token && source.includes(token)) return token;
      if (enableReverseCheck) {
        const rev = token.split('').reverse().join('');
        if (rev && source.includes(rev)) return token;
      }
    }
    return null;
  };

  const offending = containsAny(pwdSanitized) || containsAny(pwdDeLeet);
  if (offending) {
    reasons.push('Password must not contain first name or last name (or parts thereof).');
  }

  return { valid: reasons.length === 0, reasons };
};

export {
  MONTH_INDEX_DECEMBER,
  LAST_DAY_OF_DECEMBER,
  END_OF_DAY_HOUR_UTC,
  END_OF_DAY_MINUTE_UTC,
  END_OF_DAY_SECOND_UTC,
  END_OF_DAY_MILLISECOND_UTC,
  getTranslation,
  getCurrency,
  useCurrency,
  useIsLightMode,
  removeLeadingZeros,
  sanitizeDigits,
  generateTimeSeries,
  formatCompactNumber,
  isLevelizedCostOfEnergyTitle,
  formatLcoeUpToFiveDecimals,
  formatValueByChartTitle,
  formatTooltipValueByChartTitle,
  formatDisplayNumber,
  formatEnUsValue,
  roundToMaxTwoDecimals,
  toUtcISOStringWithStartTime,
  getUtcMidnightIso,
  getDefaultEvcsStartDate,
  validatePasswordAgainstNames,
};
